import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { KLSlot, ServiceWithId, UserType } from '../../../constants/types';
import { sendOtpRequest, verifyOtpRequest } from '../../../store/duckers/otp/thunk';
import { addNewCustomer, fetchCustomerDetails } from '../../../store/duckers/customers/thunk';
import { bookSlot } from '../../../store/duckers/slots/thunk';
import { verifyToken } from '../../../store/duckers/tokens/thunk';
import { StorageKeys } from '../../../constants/localStorage';

export type BookingAuthStep = 'phone' | 'otp' | 'name' | 'booking' | 'done' | 'error';

const COOLDOWN_SECONDS = 30;

interface UseBookingAuthStateArgs {
  businessId: string;
  slot: KLSlot;
  service: ServiceWithId;
}

interface UseBookingAuthStateResult {
  currentStep: BookingAuthStep;
  isSubmitting: boolean;
  error?: string;
  cooldownSeconds: number;
  onPhoneSubmit: (phone: string) => void;
  onOtpSubmit: (code: string) => void;
  onNameSubmit: (name: string) => void;
  onResend: () => void;
  isDone: boolean;
}

const readStoredToken = (): string | null => {
  const raw = localStorage.getItem(StorageKeys.VerifiedCustomerOtpToken);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as string;
  } catch {
    return null;
  }
};

const useBookingAuthState = ({ businessId, slot, service }: UseBookingAuthStateArgs): UseBookingAuthStateResult => {
  const dispatch = useDispatch<AppDispatch>();

  const tokens = useSelector((s: RootState) => s.tokens);
  const customers = useSelector((s: RootState) => s.customers);
  const slots = useSelector((s: RootState) => s.slots);
  const otp = useSelector((s: RootState) => s.otp);

  const [step, setStep] = useState<BookingAuthStep>('phone');
  const [phone, setPhone] = useState<string>('');
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
  const [error, setError] = useState<string | undefined>(undefined);
  // ref (not state) so a React StrictMode double-invocation of the effect
  // can't slip a second dispatch through before setState propagates.
  const dispatchedBookingRef = useRef<boolean>(false);
  const [bookingResult, setBookingResult] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [bookingErrorMessage, setBookingErrorMessage] = useState<string | undefined>(undefined);

  // Resolve initial step on mount based on stored token + Redux state
  useEffect(() => {
    const stored = readStoredToken();
    if (!stored) {
      setStep('phone');
      return;
    }
    if (tokens.isTokenVerified) {
      if (customers.fetchCustomerDetailsSucceeded) {
        if (customers.customer) {
          setStep('booking');
        } else {
          setStep('name');
        }
      }
      return;
    }
    if (!tokens.verifyingToken) {
      dispatch(verifyToken(stored));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cooldown ticker
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const t = setTimeout(() => setCooldownSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownSeconds]);

  // When verify-otp succeeds, persist the token and look up the customer
  // by phone. We fire this off the OTP slice directly (rather than waiting
  // for `verifyToken` to hydrate the tokens slice) because the OTP flow
  // just proved the phone number.
  useEffect(() => {
    if (step !== 'otp') return;
    if (!otp.verifyOtpRequestSucceeded || !otp.token) return;
    localStorage.setItem(StorageKeys.VerifiedCustomerOtpToken, JSON.stringify(otp.token));
    if (!customers.fetchingCustomerDetails && !customers.fetchCustomerDetailsSucceeded) {
      dispatch(fetchCustomerDetails({ businessId, phone }));
    }
  }, [
    step,
    otp.verifyOtpRequestSucceeded,
    otp.token,
    businessId,
    phone,
    customers.fetchingCustomerDetails,
    customers.fetchCustomerDetailsSucceeded,
    dispatch,
  ]);

  // Once the customer lookup returns, advance to the next step:
  // - existing customer (has `customer`) → booking
  // - new customer (lookup succeeded but no record) → name
  useEffect(() => {
    if (step !== 'otp') return;
    if (!otp.verifyOtpRequestSucceeded) return;
    if (customers.fetchingCustomerDetails) return;
    if (customers.customer) setStep('booking');
    else if (customers.fetchCustomerDetailsSucceeded) setStep('name');
  }, [
    step,
    otp.verifyOtpRequestSucceeded,
    customers.customer,
    customers.fetchingCustomerDetails,
    customers.fetchCustomerDetailsSucceeded,
  ]);

  // Booking auto-dispatch: fires when step === 'booking' AND we have customer info.
  // The ref-based guard prevents React StrictMode from firing bookSlot twice
  // (which would create two duplicate calendar events). Tracking the specific
  // dispatch's promise via unwrap() avoids reacting to stale slots.error from
  // a prior modal open or unrelated slots action.
  useEffect(() => {
    if (step !== 'booking' || dispatchedBookingRef.current) return;
    const customerName = customers.customer?.name || customers.customerName || '';
    const customerPhone = tokens.userData?.phoneNumber || customers.customerPhone || phone;
    if (!customerName || !customerPhone) return;

    dispatchedBookingRef.current = true;
    setBookingResult('pending');
    dispatch(
      bookSlot({
        businessId,
        customerName,
        customerPhone,
        service,
        startTime: slot.start,
        endTime: slot.end,
        userType: UserType.Customer,
      }),
    )
      .unwrap()
      .then(() => setBookingResult('success'))
      .catch((reason) => {
        setBookingErrorMessage(typeof reason === 'string' ? reason : undefined);
        setBookingResult('error');
      });
  }, [
    step,
    customers.customer,
    customers.customerName,
    customers.customerPhone,
    tokens.userData,
    phone,
    dispatch,
    businessId,
    service,
    slot.start,
    slot.end,
  ]);

  // Advance to done/error strictly based on this dispatch's result, ignoring
  // any pre-existing slots.error left over in Redux from other flows.
  useEffect(() => {
    if (step !== 'booking') return;
    if (bookingResult === 'success') {
      setStep('done');
    } else if (bookingResult === 'error') {
      setError(bookingErrorMessage || 'שגיאה בהזמנת התור, נסו שוב.');
      setStep('error');
    }
  }, [step, bookingResult, bookingErrorMessage]);

  const onPhoneSubmit = useCallback(
    (nextPhone: string) => {
      setPhone(nextPhone);
      setError(undefined);
      dispatch(sendOtpRequest({ phoneNumber: nextPhone, userType: UserType.Customer }));
      setCooldownSeconds(COOLDOWN_SECONDS);
      setStep('otp');
    },
    [dispatch],
  );

  const onOtpSubmit = useCallback(
    (code: string) => {
      // Guard against duplicate dispatches — the backend consumes the OTP
      // record on the first successful verify, so any re-verify with the same
      // code will return 400 "No OTP found" and surface as "משהו השתבש".
      if (otp.verifyingOtpRequest || otp.verifyOtpRequestSucceeded) return;
      setError(undefined);
      dispatch(verifyOtpRequest({ phoneNumber: phone, userType: UserType.Customer, otp: code }));
    },
    [dispatch, phone, otp.verifyingOtpRequest, otp.verifyOtpRequestSucceeded],
  );

  const onNameSubmit = useCallback(
    (name: string) => {
      setError(undefined);
      dispatch(addNewCustomer({ businessId, name, phone, userType: UserType.Customer }));
      setStep('booking');
    },
    [dispatch, businessId, phone],
  );

  const onResend = useCallback(() => {
    if (cooldownSeconds > 0) return;
    dispatch(sendOtpRequest({ phoneNumber: phone, userType: UserType.Customer }));
    setCooldownSeconds(COOLDOWN_SECONDS);
  }, [dispatch, phone, cooldownSeconds]);

  const isSubmitting = useMemo(
    () =>
      Boolean(
        slots.bookingSlot ||
          customers.addingCustomer ||
          customers.fetchingCustomerDetails ||
          tokens.verifyingToken ||
          otp.sendingOtpRequest ||
          otp.verifyingOtpRequest,
      ),
    [
      slots.bookingSlot,
      customers.addingCustomer,
      customers.fetchingCustomerDetails,
      tokens.verifyingToken,
      otp.sendingOtpRequest,
      otp.verifyingOtpRequest,
    ],
  );

  return {
    currentStep: step,
    isSubmitting,
    error,
    cooldownSeconds,
    onPhoneSubmit,
    onOtpSubmit,
    onNameSubmit,
    onResend,
    isDone: step === 'done',
  };
};

export default useBookingAuthState;
